import React from 'react';
import { get } from 'lodash';
import Spinner from 'src/assets/spinner.svg';
import ChevronDownSVG from 'src/assets/svg/chevronDown.svg';
import PrivacyEyeSVG from 'src/assets/svg/privacy-eye.svg';
import { TYPES } from 'src/componentsTailwind/Table/Table';
import appUtils from 'src/components/appUtils';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonDateUtils from 'common/commonDateUtils';
import commonGoalUtils from 'common/commonGoalUtils';
import commonPermissions from 'common/commonPermissions';
import commonTreeUtils from 'common/commonTreeUtils';
import PAGE_ROUTES from 'src/constants/pageRoutes';

const { ACCESS, GOAL_VISIBILITY } = COMMON_CONSTANTS;

const getRowOffset = (depth) => {
  const paddingValues = {
    0: 'pl-0',
    1: 'pl-[1.25rem]',
    2: 'pl-[2.5rem]',
    3: 'pl-[3.75rem]',
    4: 'pl-[5rem]',
    5: 'pl-[6.25rem]',
    6: 'pl-[7.5rem]'
  };

  return `${paddingValues[depth] || 'pl-[0rem]'}`;
};

const toggleExpandRow = async ({
  goal,
  depth,
  setExpandedGoals,
  setSubgoalFilter,
  lookupData
}) => {
  const { id: goalId, treeId } = goal;
  const baseId = goal.baseId || goalId;

  let shouldRefetch = false;
  setExpandedGoals((prevExpandedGoals) => {
    const newExpandedGoals = new Map(prevExpandedGoals);
    const isExpanded = newExpandedGoals.has(`${goalId}_${depth}`);

    if (isExpanded) {
      newExpandedGoals.delete(`${goalId}_${depth}`);
    } else {
      newExpandedGoals.set(`${goalId}_${depth}`, { id: goalId, depth });

      if (!lookupData.has(baseId)) {
        shouldRefetch = true;
        setSubgoalFilter({
          baseId: goalId,
          treeId
        });
      }
    }

    return newExpandedGoals;
  });
  return shouldRefetch;
};

const buildRows = ({
  viewerAccount,
  goalList,
  expandedGoals,
  lookupData,
  tree,
  redirectUrl = false,
  refetchGoalsLookup = () => {},
  setSubgoalFilter = () => {},
  setExpandedGoals = () => {},
  navigate = () => {},
  dateKey = 'deadline'
}) => {
  let rows = [];

  const emptyCells = [{}, {}, {}, {}, {}, {}];

  goalList.forEach((goal) => {
    const { id: goalId, depth = 0 } = goal;
    const isPrivate = goal.visibility === GOAL_VISIBILITY.PRIVATE;
    const canHaveSubgoals = commonGoalUtils.canHaveSubgoals(goal);
    const isExpanded = expandedGoals.has(`${goalId}_${depth}`);
    const baseId = goal.baseId || goalId;
    const lookupQuery = lookupData.get(baseId) || {};
    const canManageAccount = commonPermissions.canManageAccounts(
      viewerAccount,
      [goal.assignee]
    );

    const isAdmin = viewerAccount.access === ACCESS.ADMIN;

    const { data: subgoalsTree, isFetching: isFetchingSubgoals } = lookupQuery;

    const assigneeNode = goal.assignee
      ? commonTreeUtils.findNodeById(tree, goal.assignee)
      : { name: 'Unassigned' };
    const assignee = assigneeNode || { name: 'Unassigned' };

    const canManageGoal = canManageAccount
      || viewerAccount.id === goal.assignee
      || isAdmin
      || commonTreeUtils.isNodeAbove(tree, viewerAccount.id, goal.assignee)
      || (!goal.assignee && isAdmin);

    const goalProgress = commonGoalUtils.getGoalProgressLabel(goal);
    const goalStatus = commonGoalUtils.getGoalStatusLabelFromLatestUpdate(
      goal,
      viewerAccount.companyid
    );

    let goalDate = goal[dateKey];
    if (isFinite(goalDate)) {
      goalDate = new Date(goalDate * 1000);
    } else {
      goalDate = new Date(goalDate);
    }

    let icon = null;
    if (isPrivate) {
      icon = (
        <div className='h-[27px] w-[27px] flex items-center justify-center'>
          <PrivacyEyeSVG />
        </div>
      );
    } else if (canHaveSubgoals) {
      icon = (
        <div className='h-[27px] w-[27px] flex items-center justify-center'>
          <ChevronDownSVG
            height={24}
            width={24}
            className={`text-black transform ${isExpanded ? 'rotate-180' : 'rotate-90'}`}
          />
        </div>
      );
    }

    rows.push([
      {
        tooltipLabel: isPrivate ? 'Private Goal' : null,
        disabled: isPrivate || !canHaveSubgoals,
        meta: {
          goalId,
          depth
        },
        type: TYPES.ACTION,
        icon: <div className={getRowOffset(depth)}>{icon}</div>,
        onClick: async () => {
          const shouldRefetch = await toggleExpandRow({
            goal,
            depth,
            setExpandedGoals,
            setSubgoalFilter,
            lookupData
          });
          if (shouldRefetch) refetchGoalsLookup();
        }
      },
      {
        label: goal.title,
        labelClasses: getRowOffset(depth)
      },
      {
        label: assignee.name,
        labelClasses: 'pr-2'
      },
      {
        label: `${commonDateUtils.dateToMonthDayYearFormat(goalDate)}`
      },
      {
        label: goalProgress
      },
      {
        label: goalStatus
      },
      {
        type: TYPES.DROP_MENU,
        label: 'Actions',
        direction: 'left',
        ifEmpty: 'hide',
        items: [
          ...(canManageGoal
            ? [
              {
                type: TYPES.ACTION,
                label: 'Edit',
                onClick: () => navigate(
                  `/dashboard/goal/edit/${goal.assignee}/${goal._id}?redir=${
                    redirectUrl || PAGE_ROUTES.ORG_GOALS_ROUTE
                  }`
                )
              },
              {
                type: TYPES.ACTION,
                label: 'View',
                onClick: () => navigate(
                  `/dashboard/goal/view/${goal.assignee}/${goal._id}?redir=${
                    redirectUrl || PAGE_ROUTES.ORG_GOALS_ROUTE
                  }`
                )
              }
            ]
            : [])
        ]
      }
    ]);

    if (isExpanded && isFetchingSubgoals) {
      rows = rows.concat([
        [
          {
            type: TYPES.ACTION,
            classes: 'pl-8',
            icon: (
              <div className='spinner-container-xs inline-block -my-5 pl-4'>
                <Spinner />
              </div>
            )
          },
          ...emptyCells
        ]
      ]);
    }

    if (!isFetchingSubgoals) {
      const subgoals = commonGoalUtils.findGoalChildrenById(
        goalId,
        subgoalsTree
      );

      const hasSubgoals = isExpanded && subgoals.length;

      const noSubgoals = isExpanded && !subgoals.length;

      if (isExpanded && canHaveSubgoals && canManageGoal) {
        rows = rows.concat([
          [
            {},
            {
              meta: {
                goalId,
                depth
              },
              type: TYPES.ACTION,
              label: 'Add subgoal',
              classes: `${getRowOffset(depth + 1)} text-purple italic`,
              onClick: () => {
                navigate(
                  `/dashboard/goal/new/${
                    goal.assignee || appUtils.getLoggedUserId()
                  }?parentId=${goalId}`
                );
              }
            },
            ...emptyCells
          ]
        ]);
      }

      if (hasSubgoals) {
        rows = rows.concat(
          buildRows({
            viewerAccount,
            goalList: subgoals,
            expandedGoals,
            lookupData,
            tree,
            redirectUrl,
            refetchGoalsLookup,
            setSubgoalFilter,
            setExpandedGoals,
            navigate,
            dateKey
          })
        );
      }

      if (noSubgoals && !canManageGoal) {
        rows = rows.concat([
          [
            {},
            {
              label: 'No subgoals',
              labelClasses: `${getRowOffset(depth + 1)}`
            },
            ...emptyCells
          ]
        ]);
      }
    }
  });

  return rows;
};

const handleLookup = ({
  goalTree,
  isFetchingGoalsLookup,
  setLookupData = () => {},
  subgoalFilter,
  lookupData
}) => {
  const hasChildren = get(goalTree, 'children.length', 0) > 0 && !isFetchingGoalsLookup;

  if (hasChildren) {
    setLookupData((prev) => {
      const newLookupData = new Map(prev);
      newLookupData.set(subgoalFilter.baseId, {
        data: goalTree,
        isFetching: false
      });
      return newLookupData;
    });
  }
  const hasNoChildren = !get(goalTree, 'children.length', 0) > 0
    && !isFetchingGoalsLookup
    && lookupData.has(subgoalFilter.baseId);

  if (hasNoChildren) {
    setLookupData((prev) => {
      const newLookupData = new Map(prev);
      newLookupData.set(subgoalFilter.baseId, {
        data: {},
        isFetching: false
      });
      return newLookupData;
    });
  }
  const isFetchingChildren = isFetchingGoalsLookup && !lookupData.has(subgoalFilter.baseId);

  if (isFetchingChildren) {
    setLookupData((prev) => {
      const newLookupData = new Map(prev);
      newLookupData.set(subgoalFilter.baseId, {
        data: {},
        isFetching: true
      });
      return newLookupData;
    });
  }
};

export {
  getRowOffset, toggleExpandRow, buildRows, handleLookup
};
